/**
 * The message the agent is part-way through writing, drawn as a message.
 *
 * Owner's choice, of three offered: 跟真的 block 完全一樣的樣式. So this renders through
 * `Msg`, the same component every real block goes through.
 *
 * PUSHED-ONLY, since 2026-08-30. The client used to read claude's and cursor's
 * panes itself, and the owner met every failure mode that design permits: history
 * rendered as live while the agent thought (the pane still shows the previous
 * reply), and a view switch truncated the block mid-stream (the accumulator lived
 * in this component and died with it). The extraction now runs ON THE MACHINE
 * (edge paneLive.ts) where the pane, the transcript and the accumulator all live;
 * every CLI arrives here the same way codex and agy always did — as `live-text`
 * frames — and a (re)subscribe replays the accumulated text at once, which is the
 * queue the owner asked for. This component renders what the machine says and
 * holds no opinion of its own.
 */
import { useSyncExternalStore, useLayoutEffect, useRef, useState } from 'react'
import { paneVersion, pushedLiveKind, pushedLiveText, subscribePane } from './paneStore'
import { Msg, Turn, WorkingFold } from '../transcriptBlocks'
import type { TranscriptBlock } from '../../transport/api'
import { unwrap } from './reflow'
import { explainedBySettled } from './explained'

export function LiveBlock({
  lastAssistant,
  session,
  hash,
  resolve,
  describeHash,
  describeRoom,
  ownerName,
  resolutionKey,
  tail,
}: {
  /** Raw text of the newest assistant block the transcript already holds. */
  lastAssistant: string | null
  session: string
  hash: string
  resolve: (h: string) => string | null
  describeHash: ((h: string) => { name: string; machine: string } | null) | null
  describeRoom: ((id: string) => string | null) | null
  ownerName: string | null
  resolutionKey: string
  /**
   * THE IN-FLIGHT TURN — owner, 2026-09-12: 在 Agent 還在思考 / 修改代碼的時候, Show the
   * working 的位置放錯了. 應該要放在 Agent 頭像的下面. The transcript's last turn, when it
   * has steps but no conclusion yet, is the one being written; drawn on its own it is a
   * silent row ("no reply — N steps only") ABOVE this block, the fold over the avatar.
   * Handed here instead, the fold sits under this block's head while the text streams,
   * and this component draws the silent row itself when it has no text — exactly one
   * of the two ever shows. `hits`/`current` carry the search marks for that turn.
   * `working` is the pushed busy switch: with nothing live yet but the agent busy, the
   * fold is drawn under the agent's own head rather than as a bare row (owner,
   * 2026-09-12: 就算只有 Show the working 也要放一個 Agent 的頭像在上面, 這樣對於使用者更直觀).
   */
  tail?: { blocks: TranscriptBlock[]; hits: ReadonlySet<TranscriptBlock>; current: boolean; working: boolean } | undefined
}) {
  useSyncExternalStore(subscribePane, paneVersion, () => 0)
  const text = pushedLiveText()
  const [open, setOpen] = useState(false)

  /*
   * FOLLOW THE TEXT DOWN. The conversation's own auto-scroll runs when `blocks`
   * change and knows nothing about this block. Only when the reader was already at
   * the bottom: someone who scrolled up is not yanked back by every frame.
   */
  const ref = useRef<HTMLDivElement | null>(null)
  const lastHeight = useRef(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    let sc: HTMLElement | null = el.parentElement
    while (sc && !/(auto|scroll)/.test(getComputedStyle(sc).overflowY)) sc = sc.parentElement
    if (!sc) return
    const grew = sc.scrollHeight - lastHeight.current
    const wasAtBottom = sc.scrollHeight - grew - sc.scrollTop - sc.clientHeight < 80
    if (wasAtBottom) sc.scrollTop = sc.scrollHeight
    lastHeight.current = sc.scrollHeight
  }, [text])

  const tailTurn = tail ? (
    <Turn
      blocks={tail.blocks}
      session={session}
      hash={hash}
      resolve={resolve}
      {...(describeHash ? { describeHash } : {})}
      {...(describeRoom ? { describeRoom } : {})}
      ownerName={ownerName}
      resolutionKey={resolutionKey}
      hits={tail.hits}
      current={tail.current}
    />
  ) : null
  // The agent is busy with nothing said yet: its own head (avatar, name, badge) with
  // the fold under it, so a reader sees WHO is working before a word arrives. Not a
  // live block — no caret, nothing is streaming — and not the silent row, which says
  // "no reply" about a reply that is still coming.
  const waitingHead =
    tail && tail.working ? (
      <div ref={ref} className={`live-wait${open ? ' open' : ''}${tail.current ? ' hit-current' : ''}`} aria-busy="true">
        <Msg
          b={{ kind: 'assistant', text: '' }}
          session={session}
          hash={hash}
          resolve={resolve}
          describeHash={describeHash}
          describeRoom={describeRoom}
          ownerName={ownerName}
          resolutionKey={resolutionKey}
          belowHead={
            <WorkingFold
              blocks={tail.blocks}
              working={tail.blocks}
              session={session}
              hash={hash}
              resolve={resolve}
              describeHash={describeHash ?? undefined}
              describeRoom={describeRoom ?? undefined}
              ownerName={ownerName}
              resolutionKey={resolutionKey}
              hits={tail.hits}
              current={tail.current}
              open={open}
              setOpen={setOpen}
              boxRef={ref}
            />
          }
        />
      </div>
    ) : null
  // Nothing live to carry the fold: busy → the agent's head with the fold; idle → the
  // tail turn draws exactly as it always did (the silent "no reply" row).
  if (!text) return waitingHead ?? tailTurn
  /*
   * NEVER SHOW BOTH COPIES. The finished text lands in the transcript while the
   * machine may still be working, so this block has to withdraw itself.
   *
   * THE FLOOR USED TO BE 12 AND THAT IS THE BUG (owner's screenshot, 2026-09-02):
   * avg-devenv answered 「直接量,不用猜。」 and the same sentence rendered twice, the
   * second copy with no clock — this component. MEASURED: norm() of that sentence is
   * SIX characters, so the length test short-circuited and the containment test —
   * which WOULD have matched, both sides being the identical six characters — never
   * ran. Any short reply was structurally undroppable.
   *
   * WHY THE FLOOR WAS SAFE ONCE, AND STOPPED BEING. Its original comment (deleted with
   * the client-side extractor in 9e0dd53, 2026-08-30) read: "A short candidate is too
   * little to compare; a finished message is never short AND still carrying the working
   * spinner, so let it through." That held only because the OLD client refused to hold
   * a candidate at all unless the pane showed a spinner. Extraction moved to the machine
   * and this became a pure renderer of whatever was last pushed; the precondition went,
   * the floor stayed.
   *
   * THE SAME NUMBER, THE SAME SIX-CHARACTER CJK SHAPE, WAS ALREADY FIXED ON THE EDGE and
   * this copy was missed — paneLive.ts's prefix-dropper carries the ruling verbatim:
   * 「寫首長詩給我」 normalizes to six, "FOUR, not twelve, and not one", and the trade it
   * names applies here unchanged: the cost of being wrong is one short opening line
   * missing from the live block for a second, which the settled transcript then corrects;
   * the cost of the floor is a permanent duplicate on screen.
   *
   * EXACT EQUALITY HAS NO FLOOR AT ALL. If the two normalize to the same string there is
   * nothing to weigh — it is the same message, and the settled copy is the one with a
   * clock on it. The floor only guards the CONTAINMENT case, where a new reply's opening
   * words could coincidentally appear inside the previous block.
   *
   * AND CONTAINMENT IS NOT ENOUGH (owner, 2026-09-05: 會把最後一個 block 讀入,並且閃動).
   * The pane's render of a block is not always a substring of the block — a reflowed
   * table, a line broken inside a word, a tally line the machine failed to strip —
   * and every such difference drew the settled reply a second time with the caret on
   * it. The rule now lives in explained.ts and adds the machine's own shingle gate:
   * a live text mostly assembled from the settled block IS the settled block.
   */
  if (explainedBySettled(text, lastAssistant)) return waitingHead ?? tailTurn
  const kind = pushedLiveKind() === 'thinking' ? 'thinking' : 'assistant'
  const b: TranscriptBlock = { kind, text: unwrap(text) }
  // The in-flight turn's fold, under this block's head (an assistant message has a
  // head; a thinking block has none, so there it sits just above the block instead).
  const fold = tail ? (
    <WorkingFold
      blocks={tail.blocks}
      working={tail.blocks}
      session={session}
      hash={hash}
      resolve={resolve}
      describeHash={describeHash ?? undefined}
      describeRoom={describeRoom ?? undefined}
      ownerName={ownerName}
      resolutionKey={resolutionKey}
      hits={tail.hits}
      current={tail.current}
      open={open}
      setOpen={setOpen}
      boxRef={ref}
    />
  ) : null
  return (
    <div
      ref={ref}
      className={`live-block${open ? ' open' : ''}${tail?.current ? ' hit-current' : ''}`}
      aria-live="polite"
      aria-busy="true"
    >
      {kind === 'thinking' && fold && <div className="live-fold">{fold}</div>}
      <Msg
        b={b}
        session={session}
        hash={hash}
        resolve={resolve}
        describeHash={describeHash}
        describeRoom={describeRoom}
        ownerName={ownerName}
        resolutionKey={resolutionKey}
        {...(kind === 'assistant' && fold ? { belowHead: fold } : {})}
      />
    </div>
  )
}
